import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  Building2, Wrench, ShieldCheck, FileText, RefreshCw, PhoneCall,
  Cpu, CheckCircle, ArrowRight, Menu, X, Star,
  MapPin, Phone, Mail, ChevronRight, ChevronDown, Sparkles, HelpCircle,
  Send, Instagram, Linkedin
} from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  companyInfo, heroStats, marqueeItems, aboutContent, projectsContent,
  projects, servicesContent, services, trustContent, faqs,
  testimonialsContent, testimonials, ctaContent, footerContent,
  privacyPolicy, termsOfService
} from "./mock";
import { useFormspree } from "./hooks/useFormspree";

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 44 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.75, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const navLinks = [
  { label: "About Us", short: "About", href: "#about", testId: "nav-about" },
  { label: "Projects", short: "Projects", href: "#projects", testId: "nav-projects" },
  { label: "Services", short: "Services", href: "#services", testId: "nav-services" },
  { label: "Why Us", short: "Why Us", href: "#trust", testId: "nav-trust" },
  { label: "Testimonials", short: "Reviews", href: "#testimonials", testId: "nav-testimonials" }
];

const serviceIcons = { Building2, Wrench, ShieldCheck, FileText, RefreshCw, PhoneCall, Cpu };

export default function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [consultModalOpen, setConsultModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [legalModal, setLegalModal] = useState(null);

  const consultFs = useFormspree();
  const quoteFs = useFormspree();

  const [consultForm, setConsultForm] = useState({ name: "", phone: "", email: "", propertyType: "Commercial Tower / IT Park", message: "" });
  const [quoteForm, setQuoteForm] = useState({ name: "", phone: "", email: "", serviceNeeded: "Multi-Brand Elevator Maintenance (AMC)", location: "", details: "" });

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 130]);
  const heroOpacity = useTransform(scrollY, [0, 550], [1, 0.3]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (consultFs.status.state === "success") {
      const t = setTimeout(() => { setConsultModalOpen(false); consultFs.reset(); }, 4500);
      return () => clearTimeout(t);
    }
  }, [consultFs.status.state]);

  useEffect(() => {
    if (quoteFs.status.state === "success") {
      const t = setTimeout(() => { setQuoteModalOpen(false); quoteFs.reset(); }, 4500);
      return () => clearTimeout(t);
    }
  }, [quoteFs.status.state]);

  const openConsult = () => { consultFs.reset(); setConsultModalOpen(true); };
  const openQuote = () => { quoteFs.reset(); setQuoteModalOpen(true); };

  const handleConsultSubmit = async (e) => {
    e.preventDefault();
    if (!consultForm.name || !consultForm.phone) {
      toast.error("Please fill in your name and phone number.");
      return;
    }
    const ok = await consultFs.submit({
      name: consultForm.name,
      phone: consultForm.phone,
      email: consultForm.email,
      property_type: consultForm.propertyType,
      message: consultForm.message
    }, "free-consultation");
    if (ok) {
      setConsultForm({ name: "", phone: "", email: "", propertyType: "Commercial Tower / IT Park", message: "" });
    } else {
      toast.error("Could not send your request. Please try again.");
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.name || !quoteForm.phone) {
      toast.error("Please fill in your name and phone number.");
      return;
    }
    const ok = await quoteFs.submit({
      name: quoteForm.name,
      phone: quoteForm.phone,
      email: quoteForm.email,
      service_needed: quoteForm.serviceNeeded,
      location: quoteForm.location,
      project_details: quoteForm.details
    }, "get-a-quote");
    if (ok) {
      setQuoteForm({ name: "", phone: "", email: "", serviceNeeded: "Multi-Brand Elevator Maintenance (AMC)", location: "", details: "" });
    } else {
      toast.error("Could not send your request. Please try again.");
    }
  };

  const openProjectDetails = (project) => {
    setSelectedProject(project);
    setProjectModalOpen(true);
  };

  const legalDoc = legalModal === "privacy" ? privacyPolicy : legalModal === "terms" ? termsOfService : null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-blue-600 selection:text-white">
      <Toaster position="top-right" richColors />

      {/* TOP EMERGENCY BAR */}
      <div className="bg-slate-900 text-slate-200 text-xs sm:text-sm py-2 px-4 border-b border-slate-800" data-testid="top-emergency-bar">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <span className="flex items-center gap-1.5 text-blue-400 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              24×7 Service Support:
            </span>
            <a href={`tel:${companyInfo.phoneRaw}`} className="hover:text-white font-semibold transition-colors flex items-center gap-1" data-testid="top-phone-link">
              <Phone className="w-3.5 h-3.5 text-blue-400" /> {companyInfo.phone}
            </a>
            <span className="text-slate-600">|</span>
            <a href={`tel:${companyInfo.phone2Raw}`} className="hover:text-white font-semibold transition-colors" data-testid="top-phone2-link">
              {companyInfo.phone2}
            </a>
          </div>
          <div className="flex items-center gap-4 text-slate-300">
            <span className="hidden md:inline text-slate-400">{companyInfo.dealerBadge}</span>
            <span className="text-blue-400 font-medium bg-blue-950/80 px-2 py-0.5 rounded border border-blue-800/50">{companyInfo.rankBadge}</span>
          </div>
        </div>
      </div>

      {/* NAVBAR */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-3' : 'bg-white py-4 sm:py-5 border-b border-slate-100'}`} data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <a href="#" className="flex items-center gap-2.5 group" data-testid="nav-logo-link">
            <img
              src="/images/smart-levo-logo.png"
              alt="Smart Levo India Logo"
              className="w-10 h-10 object-contain rounded-lg group-hover:scale-105 transition-transform"
            />
            <div>
              <span className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
                SMART LEVO <span className="text-blue-600">INDIA</span>
              </span>
              <span className="block text-[10px] tracking-widest uppercase text-slate-500 font-semibold -mt-1">
                Authorised Supplier of Canny Elevators
              </span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <nav className="hidden xl:flex items-center gap-8 font-medium text-sm text-slate-700" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="hover:text-blue-600 transition-colors" data-testid={link.testId}>{link.label}</a>
            ))}
          </nav>

          {/* Tablet Nav Links (Compact) */}
          <nav className="hidden lg:flex xl:hidden items-center gap-4 font-medium text-xs text-slate-700" data-testid="tablet-nav">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="hover:text-blue-600 transition-colors">{link.short}</a>
            ))}
          </nav>

          {/* Action CTAs */}
          <div className="hidden sm:flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2.5 pr-3 border-r border-slate-200" title="Authorised Dealer & Supplier of Canny Elevators">
              <img
                src="/images/canny-logo.jpeg"
                alt="Canny Elevator"
                className="h-9 w-auto object-contain bg-white rounded-md border border-slate-200 px-1.5 py-0.5"
                data-testid="nav-canny-logo"
              />
            </div>
            <button
              onClick={openConsult}
              className="px-4 py-2.5 rounded-lg border border-blue-600 text-blue-600 font-semibold text-sm hover:bg-blue-50 transition-all shadow-sm"
              data-testid="nav-consultation-btn"
            >
              Free Consultation
            </button>
            <button
              onClick={openQuote}
              className="px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-all shadow-md shadow-blue-600/20"
              data-testid="nav-quote-btn"
            >
              Get A Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-slate-700 hover:text-blue-600 focus:outline-none"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-slate-200 shadow-xl py-6 px-6 flex flex-col gap-4 animate-in slide-in-from-top-2" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} onClick={() => setMobileMenuOpen(false)} className="text-slate-800 font-medium py-2 border-b border-slate-100">{link.label}</a>
            ))}
            <div className="flex flex-col gap-3 pt-2">
              <button
                onClick={() => { setMobileMenuOpen(false); openConsult(); }}
                className="w-full py-3 rounded-lg border border-blue-600 text-blue-600 font-semibold text-center"
              >
                Free Consultation
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); openQuote(); }}
                className="w-full py-3 rounded-lg bg-blue-600 text-white font-semibold text-center shadow-md"
              >
                Get A Quote
              </button>
            </div>
            <div className="flex items-center gap-3 pt-4 mt-1 border-t border-slate-100" data-testid="mobile-menu-canny-badge">
              <img
                src="/images/canny-logo.jpeg"
                alt="Canny Elevator"
                className="h-10 w-auto object-contain bg-white rounded-md border border-slate-200 px-1.5 py-0.5"
              />
              <span className="text-xs font-semibold text-slate-500 leading-snug">Authorised Dealer & Supplier of Canny Elevators</span>
            </div>
          </div>
        )}
      </header>

      {/* ================= HERO SECTION ================= */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-slate-950 text-white" data-testid="hero-section">
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/70 to-slate-950/40 z-10"></div>
          <div className="absolute inset-0 bg-blue-950/30 mix-blend-multiply z-10"></div>
          <iframe
            src="https://www.youtube.com/embed/bc4Oq5cM0sQ?autoplay=1&mute=1&loop=1&playlist=bc4Oq5cM0sQ&controls=0&showinfo=0&autohide=1&modestbranding=1&disablekb=1&iv_load_policy=3"
            title="Canny Elevators Luxury Showcase"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] h-[56.25vw] min-h-[100vh] min-w-[177.77vh] object-cover opacity-80"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          ></iframe>
        </div>

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 w-full">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/30 border border-blue-500/40 text-blue-300 text-xs sm:text-sm font-semibold mb-6 backdrop-blur-md shadow-lg"
              data-testid="hero-badge"
            >
              <Sparkles className="w-4 h-4 text-blue-300" />
              <span>{companyInfo.rankBadge}</span>
            </motion.div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white mb-6 leading-[1.05]" data-testid="hero-heading">
              <MaskedLine delay={0.15}>Smart Levo India</MaskedLine>
              <MaskedLine delay={0.32} className="text-blue-500">Pvt Ltd</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
              className="text-lg sm:text-xl lg:text-2xl text-slate-300 font-light mb-10 leading-relaxed"
              data-testid="hero-subheading"
            >
              {companyInfo.tagline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-12"
            >
              <button
                onClick={openConsult}
                className="px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-base transition-all transform hover:-translate-y-0.5 shadow-xl shadow-blue-600/40 flex items-center justify-center gap-2 group"
                data-testid="hero-primary-cta"
              >
                <span>Request a Free Consultation</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#services"
                className="px-8 py-4 rounded-xl bg-white/10 hover:bg-white/25 text-white font-semibold text-base transition-all backdrop-blur-md border border-white/20 flex items-center justify-center gap-2"
                data-testid="hero-secondary-cta"
              >
                <span>Explore Our Services</span>
                <ChevronRight className="w-5 h-5" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-6 border-t border-white/15"
              data-testid="hero-highlights"
            >
              <div className="flex items-center gap-2.5 bg-slate-900/60 p-3 rounded-lg border border-slate-800 backdrop-blur-sm">
                <CheckCircle className="w-5 h-5 text-blue-400 shrink-0" />
                <span className="text-xs sm:text-sm font-medium text-slate-200">Professional Multi-Brand Elevator Maintenance</span>
              </div>
              <div className="flex items-center gap-2.5 bg-slate-900/60 p-3 rounded-lg border border-slate-800 backdrop-blur-sm">
                <CheckCircle className="w-5 h-5 text-blue-400 shrink-0" />
                <span className="text-xs sm:text-sm font-medium text-slate-200">Authorised Dealer & Supplier of Canny Elevators</span>
              </div>
              <div className="flex items-center gap-2.5 bg-slate-900/60 p-3 rounded-lg border border-slate-800 backdrop-blur-sm">
                <CheckCircle className="w-5 h-5 text-blue-400 shrink-0" />
                <span className="text-xs sm:text-sm font-medium text-slate-200">Canny Ranked #9 Elevator Brand Worldwide (2025)</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ================= EDITORIAL MARQUEE ================= */}
      <div className="bg-slate-950 border-y border-slate-800/80 py-4 overflow-hidden" data-testid="marquee-strip">
        <div className="flex whitespace-nowrap animate-marquee will-change-transform">
          {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, idx) => (
            <span key={idx} className="flex items-center gap-8 pr-8 text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-slate-400">
              {item}
              <span className="w-2 h-2 rotate-45 bg-blue-600 inline-block"></span>
            </span>
          ))}
        </div>
      </div>

      {/* ================= STATISTICS BANNER ================= */}
      <section className="bg-blue-600 text-white py-12 relative overflow-hidden shadow-2xl" data-testid="stats-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500 to-blue-700 opacity-90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {heroStats.map((stat, idx) => (
              <Reveal key={idx} delay={idx * 0.08}>
                <div className="p-4 rounded-xl bg-blue-700/40 border border-blue-400/30 backdrop-blur-sm" data-testid={stat.testId}>
                  <div className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-2 text-white">{stat.value}</div>
                  <div className="text-sm sm:text-base font-medium text-blue-100">{stat.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ABOUT / WHY CHOOSE US ================= */}
      <section id="about" className="py-24 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-6">
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 group">
                  <img
                    src="/images/about-engineers.jpeg"
                    alt="Smart Levo engineers performing professional elevator maintenance"
                    className="w-full h-[460px] object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent flex items-end p-8">
                    <div className="text-white">
                      <p className="text-xs uppercase font-bold tracking-widest text-blue-400 mb-1">{aboutContent.overlayLabel}</p>
                      <p className="text-lg font-semibold">{aboutContent.overlayText}</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-6 -right-6 bg-blue-900 text-white p-6 rounded-2xl shadow-xl hidden sm:block border border-blue-800">
                  <div className="text-3xl font-bold text-blue-400 mb-1">{aboutContent.highlightNumber}</div>
                  <div className="text-xs font-medium text-slate-300">{aboutContent.highlightText}</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-blue-50 text-blue-600 font-semibold text-xs uppercase tracking-wider mb-4">
                {aboutContent.topLabel}
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-6" data-testid="about-title">
                {aboutContent.heading}
              </h2>
              <p className="text-slate-600 text-base sm:text-lg mb-8 leading-relaxed">
                {aboutContent.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 mb-8">
                {[...aboutContent.pointersLeft, ...aboutContent.pointersRight].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
                      <ChevronRight className="w-3.5 h-3.5 stroke-[3]" />
                    </div>
                    <div>
                      <div className="text-slate-900 text-sm font-bold">{item.title}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={openQuote}
                  className="px-6 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-all shadow-lg shadow-blue-600/20 flex items-center gap-2"
                  data-testid="about-cta"
                >
                  <span>{aboutContent.button}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= FEATURED ELEVATOR PROJECTS ================= */}
      <section id="projects" className="py-24 bg-slate-100/70 border-t border-b border-slate-200/60" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-blue-600 font-bold text-xs uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-md">
              {projectsContent.topLabel}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 mb-4">
              {projectsContent.heading}
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              {projectsContent.intro}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, idx) => (
              <Reveal key={project.id} delay={(idx % 3) * 0.08}>
                <div
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1.5 border border-slate-200/80 flex flex-col group cursor-pointer h-full"
                  onClick={() => openProjectDetails(project)}
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="relative h-60 overflow-hidden bg-slate-900">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-95 group-hover:opacity-100"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold shadow-lg">
                        {project.badge}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3 bg-slate-950/80 backdrop-blur-md px-3 py-1 rounded-md text-white text-xs font-semibold">
                      {project.capacity}
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors mb-1">
                        {project.title}
                      </h3>
                      <p className="text-xs font-semibold text-blue-600 mb-3">{project.subtitle}</p>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {project.description}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500">View Project Details</span>
                      <span className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all">
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SERVICES SECTION ================= */}
      <section id="services" className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-blue-600 font-bold text-xs uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-md">
              {servicesContent.topLabel}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 mb-4">
              {servicesContent.heading}
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              {servicesContent.intro}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => {
              const IconComponent = serviceIcons[service.icon] || Wrench;
              return (
                <Reveal key={service.id} delay={(idx % 4) * 0.06}>
                  <div
                    className="p-6 rounded-2xl bg-slate-50 hover:bg-white border border-slate-200/80 hover:border-blue-500/50 shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col justify-between h-full"
                    data-testid={`service-card-${service.id}`}
                  >
                    <div>
                      <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-600/20 group-hover:scale-110 transition-transform">
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                          {service.badge}
                        </span>
                      </div>

                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors mb-2">
                        {service.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <button
                      onClick={openQuote}
                      className="text-xs font-bold text-blue-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                      data-testid={`service-inquire-${service.id}`}
                    >
                      <span>Inquire Now</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= WHY PEOPLE TRUST US ================= */}
      <section id="trust" className="py-24 bg-slate-950 text-white relative overflow-hidden" data-testid="trust-section">
        <div className="absolute inset-0 bg-blue-950/20 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7">
              <span className="text-blue-400 font-bold text-xs uppercase tracking-widest bg-blue-950 px-3 py-1 rounded-md border border-blue-800">
                {trustContent.topLabel}
              </span>
              <h2 className="text-3xl sm:text-5xl font-extrabold text-white mt-3 mb-6">
                {trustContent.heading}
              </h2>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                {trustContent.description}
              </p>
            </div>
            <div className="lg:col-span-5 flex justify-lg-end">
              <button
                onClick={openConsult}
                className="px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-base transition-all shadow-xl shadow-blue-600/30"
                data-testid="trust-consult-btn"
              >
                {trustContent.cta}
              </button>
            </div>
          </Reveal>

          <Reveal className="max-w-4xl mx-auto bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-400" />
              Frequently Asked Questions
            </h3>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-slate-800 pb-4 last:border-0 last:pb-0">
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className="w-full flex justify-between items-center text-left py-2 text-white font-semibold hover:text-blue-400 transition-colors"
                    data-testid={`faq-question-${index}`}
                  >
                    <span className="text-base sm:text-lg">{faq.question}</span>
                    <span className={`w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 transition-transform ${activeFaq === index ? 'rotate-180 bg-blue-600' : ''}`}>
                      <ChevronDown className="w-4 h-4 text-white" />
                    </span>
                  </button>
                  {activeFaq === index && (
                    <p className="text-slate-400 text-sm sm:text-base mt-2 pb-2 leading-relaxed animate-in fade-in-50">
                      {faq.answer}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section id="testimonials" className="py-24 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-blue-600 font-bold text-xs uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-md">
              {testimonialsContent.topLabel}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 mb-4">
              {testimonialsContent.heading}
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              {testimonialsContent.intro}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((t, idx) => (
              <Reveal key={t.id} delay={(idx % 4) * 0.08}>
                <div
                  className="bg-white p-6 rounded-2xl shadow-md border border-slate-200 flex flex-col justify-between hover:shadow-xl transition-shadow h-full"
                  data-testid={`testimonial-card-${t.id}`}
                >
                  <div>
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(t.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 text-sm italic mb-6 leading-relaxed">
                      "{t.comment}"
                    </p>
                  </div>
                  <div className="pt-4 border-t border-slate-100">
                    <div className="font-bold text-slate-900 text-sm">{t.role}</div>
                    <div className="text-xs text-blue-600 font-medium">{t.propertyType}</div>
                    <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {t.location}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CALL TO ACTION BANNER ================= */}
      <section className="py-20 bg-blue-600 relative overflow-hidden text-white" data-testid="cta-banner">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-blue-600 to-blue-800 opacity-95"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-3xl">
          <Reveal>
            <span className="bg-blue-500/50 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-blue-400/40 mb-4 inline-block">
              {ctaContent.topLabel}
            </span>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-6">
              {ctaContent.heading}
            </h2>
            <p className="text-blue-100 text-base sm:text-xl mb-10 leading-relaxed">
              {ctaContent.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={openQuote}
                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-slate-950 hover:bg-slate-900 text-white font-bold text-base transition-all shadow-2xl flex items-center justify-center gap-2"
                data-testid="cta-get-free-quote-btn"
              >
                <span>Get Free Quote</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={openConsult}
                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white hover:bg-blue-50 text-blue-600 font-bold text-base transition-all shadow-lg flex items-center justify-center gap-2"
                data-testid="cta-consultation-btn"
              >
                <span>Request Consultation</span>
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="bg-slate-950 text-slate-400 py-16 border-t border-slate-800" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
            <div className="lg:col-span-2">
              <a href="#" className="flex items-center gap-2.5 mb-6">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg p-1">
                  <img src="/images/smart-levo-logo.png" alt="Smart Levo India Logo" className="w-full h-full object-contain" />
                </div>
                <div>
                  <span className="text-xl font-extrabold tracking-tight text-white">
                    SMART LEVO <span className="text-blue-500">INDIA</span>
                  </span>
                  <span className="block text-[10px] tracking-widest uppercase text-slate-400 font-semibold -mt-1">
                    {footerContent.tagline}
                  </span>
                </div>
              </a>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed max-w-sm">
                {footerContent.description}
              </p>
              <div className="flex items-center gap-3">
                <a href="https://www.instagram.com/smartlevoindia" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-lg bg-slate-900 hover:bg-blue-600 hover:text-white flex items-center justify-center text-slate-300 transition-colors border border-slate-800" data-testid="footer-instagram-link">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.linkedin.com/company/smart-levo-india/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-lg bg-slate-900 hover:bg-blue-600 hover:text-white flex items-center justify-center text-slate-300 transition-colors border border-slate-800" data-testid="footer-linkedin-link">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href={`mailto:${companyInfo.email}`} aria-label="Email" className="w-10 h-10 rounded-lg bg-slate-900 hover:bg-blue-600 hover:text-white flex items-center justify-center text-slate-300 transition-colors border border-slate-800" data-testid="footer-email-link">
                  <Mail className="w-5 h-5" />
                </a>
                <a href={`tel:${companyInfo.phoneRaw}`} aria-label="Phone" className="w-10 h-10 rounded-lg bg-slate-900 hover:bg-blue-600 hover:text-white flex items-center justify-center text-slate-300 transition-colors border border-slate-800" data-testid="footer-phone-link">
                  <Phone className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Quick Links</h4>
              <ul className="space-y-3 text-sm">
                {footerContent.quickLinks.map((link) => (
                  <li key={link.href + link.label}><a href={link.href} className="hover:text-blue-400 transition-colors">{link.label}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Our Location</h4>
              <div className="rounded-xl overflow-hidden border border-slate-800 h-48 shadow-lg" data-testid="footer-map">
                <iframe
                  src="https://maps.google.com/maps?q=27%2C%20Ali%20Bilal%20Siddi%20CHSL%2C%20Alidada%20Estate%2C%20S.G.%20Barve%20Marg%2C%20Near%20Bus%20Depot%2C%20Kurla%20East%2C%20Mumbai%2C%20Maharashtra%20400024&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                  title="Smart Levo India Pvt Ltd - Kurla East Office Location"
                ></iframe>
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Contact Us</h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                  <span>
                    <a href={`tel:${companyInfo.phoneRaw}`} className="hover:text-blue-400 transition-colors block">{companyInfo.phone}</a>
                    <a href={`tel:${companyInfo.phone2Raw}`} className="hover:text-blue-400 transition-colors block">{companyInfo.phone2}</a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                  <span>
                    <a href={`mailto:${companyInfo.email}`} className="hover:text-blue-400 transition-colors block">{companyInfo.email}</a>
                    <a href={`mailto:${companyInfo.email2}`} className="hover:text-blue-400 transition-colors block">{companyInfo.email2}</a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                  <span>{companyInfo.address}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-900 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs">
            <p>{footerContent.copyright}</p>
            <div className="flex gap-6">
              <button onClick={() => setLegalModal("privacy")} className="hover:text-blue-400 transition-colors" data-testid="footer-privacy-link">Privacy Policy</button>
              <button onClick={() => setLegalModal("terms")} className="hover:text-blue-400 transition-colors" data-testid="footer-terms-link">Terms of Service</button>
            </div>
          </div>
        </div>
      </footer>

      {/* ================= CONSULTATION MODAL ================= */}
      {consultModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in" data-testid="consultation-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative border border-slate-200 max-h-[90vh] overflow-y-auto overscroll-contain" data-lenis-prevent>
            <button
              onClick={() => setConsultModalOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"
              data-testid="close-consult-modal"
            >
              <X className="w-5 h-5" />
            </button>

            {consultFs.status.state === "success" ? (
              <div className="text-center py-10" data-testid="consult-success">
                <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Request Sent Successfully</h3>
                <p className="text-slate-600 text-sm">{consultFs.status.message}</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2.5 py-1 rounded">Expert Engineering</span>
                  <h3 className="text-2xl font-extrabold text-slate-900 mt-2">Request a Free Consultation</h3>
                  <p className="text-slate-600 text-sm mt-1">Speak with our senior elevator consultants for new installs or multi-brand maintenance audits.</p>
                </div>

                <form onSubmit={handleConsultSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Your Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={consultForm.name}
                      onChange={(e) => setConsultForm({ ...consultForm, name: e.target.value })}
                      placeholder="e.g. Rajesh Sharma"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                      required
                      data-testid="consult-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={consultForm.phone}
                        onChange={(e) => setConsultForm({ ...consultForm, phone: e.target.value })}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                        required
                        data-testid="consult-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={consultForm.email}
                        onChange={(e) => setConsultForm({ ...consultForm, email: e.target.value })}
                        placeholder="name@company.com"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                        data-testid="consult-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Property Type</label>
                    <select
                      name="property_type"
                      value={consultForm.propertyType}
                      onChange={(e) => setConsultForm({ ...consultForm, propertyType: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm bg-white"
                      data-testid="consult-property-select"
                    >
                      <option value="Commercial Tower / IT Park">Commercial Tower / IT Park</option>
                      <option value="Residential Society / Apartment">Residential Society / Apartment</option>
                      <option value="Hospital / Healthcare">Hospital / Healthcare</option>
                      <option value="Hotel / Hospitality">Hotel / Hospitality</option>
                      <option value="Luxury Villa / Private Home">Luxury Villa / Private Home</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Message / Requirements</label>
                    <textarea
                      rows="3"
                      name="message"
                      value={consultForm.message}
                      onChange={(e) => setConsultForm({ ...consultForm, message: e.target.value })}
                      placeholder="Tell us about your elevator requirements or maintenance needs..."
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                      data-testid="consult-message-input"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={consultFs.status.state === "submitting"}
                    className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm shadow-lg shadow-blue-600/30 transition-all flex items-center justify-center gap-2"
                    data-testid="consult-submit-btn"
                  >
                    <span>{consultFs.status.state === "submitting" ? "Sending…" : "Submit Consultation Request"}</span>
                    <Send className="w-4 h-4" />
                  </button>

                  {consultFs.status.state === "error" && (
                    <p className="text-red-600 text-xs font-medium text-center" data-testid="consult-error">{consultFs.status.message}</p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ================= QUOTE MODAL ================= */}
      {quoteModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in" data-testid="quote-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative border border-slate-200 max-h-[90vh] overflow-y-auto overscroll-contain" data-lenis-prevent>
            <button
              onClick={() => setQuoteModalOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"
              data-testid="close-quote-modal"
            >
              <X className="w-5 h-5" />
            </button>

            {quoteFs.status.state === "success" ? (
              <div className="text-center py-10" data-testid="quote-success">
                <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Quote Request Sent</h3>
                <p className="text-slate-600 text-sm">{quoteFs.status.message}</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2.5 py-1 rounded">Instant Estimate</span>
                  <h3 className="text-2xl font-extrabold text-slate-900 mt-2">Get Free Quote & AMC Estimate</h3>
                  <p className="text-slate-600 text-sm mt-1">Receive transparent pricing for Canny elevators or multi-brand AMC maintenance contracts.</p>
                </div>

                <form onSubmit={handleQuoteSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Your Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                      placeholder="e.g. Priya Nair"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                      required
                      data-testid="quote-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                        required
                        data-testid="quote-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">City / Location *</label>
                      <input
                        type="text"
                        name="location"
                        value={quoteForm.location}
                        onChange={(e) => setQuoteForm({ ...quoteForm, location: e.target.value })}
                        placeholder="e.g. Mumbai, Pune"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                        required
                        data-testid="quote-location-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Service Required</label>
                    <select
                      name="service_needed"
                      value={quoteForm.serviceNeeded}
                      onChange={(e) => setQuoteForm({ ...quoteForm, serviceNeeded: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm bg-white"
                      data-testid="quote-service-select"
                    >
                      <option value="Multi-Brand Elevator Maintenance (AMC)">Multi-Brand Elevator Maintenance (AMC)</option>
                      <option value="New Canny Elevator Installation">New Canny Elevator Installation</option>
                      <option value="Elevator Modernization & Upgrade">Elevator Modernization & Upgrade</option>
                      <option value="Emergency Repair & Spare Parts">Emergency Repair & Spare Parts</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Project Details (Floors, Lifts Count)</label>
                    <textarea
                      rows="3"
                      name="project_details"
                      value={quoteForm.details}
                      onChange={(e) => setQuoteForm({ ...quoteForm, details: e.target.value })}
                      placeholder="e.g. G+5 building with 2 elevators needing comprehensive AMC..."
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-blue-600 text-sm"
                      data-testid="quote-details-input"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={quoteFs.status.state === "submitting"}
                    className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm shadow-lg shadow-blue-600/30 transition-all flex items-center justify-center gap-2"
                    data-testid="quote-submit-btn"
                  >
                    <span>{quoteFs.status.state === "submitting" ? "Sending…" : "Get Free Quotation Now"}</span>
                    <Send className="w-4 h-4" />
                  </button>

                  {quoteFs.status.state === "error" && (
                    <p className="text-red-600 text-xs font-medium text-center" data-testid="quote-error">{quoteFs.status.message}</p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ================= PROJECT DETAILS MODAL ================= */}
      {projectModalOpen && selectedProject && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in" data-testid="project-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overscroll-contain shadow-2xl relative border border-slate-200" data-lenis-prevent>
            <button
              onClick={() => setProjectModalOpen(false)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-slate-900/80 hover:bg-slate-900 text-white flex items-center justify-center transition-colors shadow-lg"
              data-testid="close-project-modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative h-72 bg-slate-950">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent flex items-end p-6">
                <div>
                  <span className="px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold mb-2 inline-block">
                    {selectedProject.badge}
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
                    {selectedProject.title}
                  </h3>
                  <p className="text-blue-400 font-medium text-sm">{selectedProject.subtitle}</p>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Overview</h4>
                <p className="text-slate-700 text-base leading-relaxed">
                  {selectedProject.overview}
                </p>
              </div>

              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Key Technical Specifications</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedProject.specs.map((spec, idx) => (
                    <div key={idx} className="flex items-center gap-2.5 bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <CheckCircle className="w-4 h-4 text-blue-600 shrink-0" />
                      <span className="text-sm font-medium text-slate-800">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 flex justify-between items-center gap-4 flex-wrap">
                <div>
                  <span className="text-xs font-bold text-blue-600 uppercase block">Capacity & Load Rating</span>
                  <span className="text-slate-900 font-bold text-base">{selectedProject.capacity}</span>
                </div>
                <button
                  onClick={() => { setProjectModalOpen(false); openQuote(); }}
                  className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-md transition-all"
                  data-testid="project-modal-quote-btn"
                >
                  Request Quote for this Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= LEGAL MODAL (PRIVACY / TERMS) ================= */}
      {legalDoc && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in" data-testid="legal-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto overscroll-contain shadow-2xl relative border border-slate-200 p-6 sm:p-10" data-lenis-prevent>
            <button
              onClick={() => setLegalModal(null)}
              className="sticky top-0 float-right w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors z-10"
              data-testid="close-legal-modal"
            >
              <X className="w-5 h-5" />
            </button>

            <span className="text-xs font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2.5 py-1 rounded">Legal</span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-3 mb-1">{legalDoc.title}</h3>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-6">Effective Date: {legalDoc.effectiveDate}</p>

            <div className="space-y-4 mb-8">
              {legalDoc.intro.map((p, idx) => (
                <p key={idx} className="text-slate-600 text-sm leading-relaxed">{p}</p>
              ))}
            </div>

            <div className="space-y-8">
              {legalDoc.sections.map((section, sIdx) => (
                <div key={sIdx}>
                  <h4 className="text-base font-extrabold text-slate-900 mb-3">{section.heading}</h4>
                  <div className="space-y-3">
                    {section.blocks.map((block, bIdx) =>
                      block.list ? (
                        <ul key={bIdx} className="list-disc pl-5 space-y-1.5 text-slate-600 text-sm">
                          {block.list.map((item, iIdx) => (
                            <li key={iIdx}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p key={bIdx} className="text-slate-600 text-sm leading-relaxed">{block.p}</p>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================= WHATSAPP FLOATING BUTTON ================= */}
      <a
        href="https://wa.me/917700971812?text=Hello%20Smart%20Levo%20India%2C%20I%20would%20like%20to%20enquire%20about%20your%20elevator%20services."
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with Smart Levo India on WhatsApp"
        className="fixed bottom-5 right-5 z-40 w-14 h-14 group"
        data-testid="whatsapp-float-btn"
      >
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-40 animate-ping [animation-duration:2.5s]"></span>
        <span className="relative flex w-14 h-14 rounded-full bg-[#25D366] shadow-xl shadow-[#25D366]/40 items-center justify-center group-hover:scale-110 transition-transform">
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </span>
      </a>

    </div>
  );
}
